import { api } from "@/comn";
import { comnUtils } from "@/comn/utils";
import { colEnvs, colUtils } from "@/tz/go/egaz/osci/colt/comn"; // 시스템 공통 유틸
import { TFormSchema, TGridSchema } from "@/comn/hooks";
import { lpcoEnvs } from "@/tz/go/egaz/osci/lpco/comn";

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 결로 Array
 */
export const BASE = {
    path: `${lpcoEnvs.base}/colt/cncl`,
    api: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/colt/cncl`,
    api_v1: `${process.env.REACT_APP_API_OSCI}/api/v1/osci/lpco/fee`,
    nodes: [
        { path: "/", label: "Collection" },
        { path: "/colt", label: "Notice" },
        { path: "/colt/cncl", label: "Cancellation" },
    ],
};

/*
 * @ URLS
 * @ 화면에서 사용하는 주소들 정의
 * @ 해당화면의 컴포넌트명 대문자로 정의
 */
export const URLS = {
    osci0403001q: `/osci/colt/cncl/osci0403001q`, // 고지 취소 목록 조회
    osci0403002q: `/osci/colt/cncl/osci0403002q`, // 고지 취소 상세 조회
    osci0403003s: `/osci/colt/cncl/osci0403003s`, // 고지 취소 내역 등록
    coli0104004q: `/osci/colt/cncl/coli0104004q`, // 고지 취소 목록 조회 for Cargo
    coli0104005q: `/osci/colt/cncl/coli0104005q`, // 고지 취소 상세 조회 for Cargo
    coli0104006s: `/osci/colt/cncl/coli0104006s`, // 고지 취소 내역 등록 for Cargo
    clri0801029q: `${process.env.REACT_APP_URL_CLRI}/info/dclr/clri0801029q`, // Declaration 처리이력 (팝업)
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    // Get Bill Cancellation List !== 고지 취소 목록 조회 ==!
    getBllCnclList: (data: any, cagYn: any, page: number, size: number) => {
        return api.get(`${BASE.api}/${cagYn}?page=${page}&size=${size}`, {
            params: comnUtils.toGetParams(data),
        });
    },
    // Get Bill Cancellation Detail !== 고지 취소 상세 조회 ==!
    getBllCnclDtl: (data: any) => {
        return api.get(`${BASE.api}/bll`, {
            params: comnUtils.toGetParams(data),
        });
        // return api.get(`${BASE.api}/${billNo}/${cagRgsrYn}`);
    },
    // Save Bill Cancellation !== 고지 취소 저장 ==!
    saveBllCncl: (dtlData: any, cnclData: any, mode: any) => {
        return api.post(`${BASE.api}/saveBllCncl/${mode}`, {
            ...dtlData,
            ...cnclData,
        });
    },
    // Delete Bill Cancellation !== 고지 취소내역 삭제 ==!
    deleteBllCncl: (bllMgmtId: any, cnclRqstSn?: any) => {
        return api.delete(`${BASE.api}/deleteBllCncl/${bllMgmtId}`, { params: { cnclRqstSn } });
    },
    // Bill Cancellation Approval Request After !== 고지 취소 결재 상신 후처리 ==!
    bllCnclAprvRqstAf: (data: any) => {
        return api.post(`${BASE.api}/bllCnclAprvRqstAf`, {
            ...data,
        });
    },
    cnclBll: (data: any) => {
        return api.post(`${BASE.api_v1}/cnclBll`, {
            ...data,
        });
    },
    reissBll: (data: any) => {
        return api.post(`${BASE.api_v1}/reissBll`, {
            ...data,
        });
    },
};

/*
 * @ 그리드 스키마 정의
 * @ 화면에서 사용하는 그리드 구조 정의
 * @ SG_ 로 시작 (Schema grid)
 * @ SG_[그리드명과 조회하는 데이터 이름 메타 단어(용어) 대문자, "_" 로 구분 , 그리드명 맨 뒤에 "_LIST" 로 종료]
 */
//============================================================================================================================
// 목록
//============================================================================================================================
// 고지 취소 목록
export const SG_BLL_CNCL_LIST: TGridSchema = {
    id: "form_bllCnclList",
    options: {
        pagination: "out", // 그리드 데이터 페이징 "out" : 데이터를 외부에서 페이징해서 가져오는 경우 , "in" : 데이터를 전체 가져와서 그리드 내부에서 페이징 및 편집
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: 500,
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 160 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 120 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_TXPR", binding: "pyerMgmtNo", width: 150 }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: "*" }] },
        //{ cells: [{ header: "L_CNCL_CD", binding: "dspoCd", width: "*" }] },
        { cells: [{ header: "L_CNCL_DT", binding: "dspoDt", width: 120 }] },
        { cells: [{ header: "L_RGST_ID", binding: "sbmsId", width: 120 }] },
        // { cells: [{ header: "L_APRV_STAT_CD", binding: "aprvPrcssStatCd", width: 150 }] },
        { cells: [{ header: "L_APRV_STAT_CD", binding: "cnclStatCd", width: 170 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt", type: "date" }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "pyerMgmtNo", mask: colUtils.getMaskPattern("tin") }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        //{ cells: [{ binding: "dspoCd", type: "code", area: "comnCd", comnCd: "OSC0057" }] },
        { cells: [{ binding: "dspoDt", type: "date" }] },
        { cells: [{ binding: "sbmsId" }] },
        // { cells: [{ binding: "aprvPrcssStatCd", type: "code", area: "comnCd", comnCd: "OSC0096" }] },
        { cells: [{ binding: "cnclStatCd", type: "code", area: "comnCd", comnCd: "OSC0252" }] },
    ],
};

// 고지 취소 상세 > 고지목록
export const SG_BLL_CNCL_DTL_BLL_LIST: TGridSchema = {
    id: "form_bllCnclDtlBllList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 250 }] },
        { cells: [{ header: "L_BLL_DT", binding: "bllDt", width: 150 }] },
        { cells: [{ header: "L_DUE_DT", binding: "lastPayDdlnDt", width: 150 }] },
        { cells: [{ header: "L_BLL_TAX_AMT", binding: "totBllTaxAmt", width: 170 }] },
        { cells: [{ header: "L_BLL_STAT", binding: "bllStatCd", width: 250 }] },
        { cells: [{ header: "L_CNCL_YN", binding: "dspoYn", width: 100 }] },
        { cells: [{ header: "L_CNCL_CD", binding: "dspoCd", width: "*" }] },
        { cells: [{ header: "L_CNCL_DT", binding: "dspoDt", width: 150 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllDt" }] },
        { cells: [{ binding: "lastPayDdlnDt" }] },
        {
            cells: [{ binding: "totBllTaxAmt", type: "number", thousandSeparator: true, align: "right" }],
        },
        { cells: [{ binding: "bllStatCd", type: "code", area: "comnCd", comnCd: "OSC0056" }] },
        { cells: [{ binding: "dspoYn" }] },
        { cells: [{ binding: "dspoCd", type: "code", area: "comnCd", comnCd: "OSC0057" }] },
        { cells: [{ binding: "dspoDt", type: "date" }] },
    ],
};

export const SG_BLL_CNCL_DTL_ITM_LIST: TGridSchema = {
    id: "grid_blldDtlList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: "*" }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: "*" }] },
        { cells: [{ header: "L_GFS_CD", binding: "gfsCd", width: "*" }] },
        { cells: [{ header: "L_ITM_NM", binding: "itmNm", width: "2*" }] },
        { cells: [{ header: "L_AMT", binding: "bllItmAmt", width: "*" }] },
        { cells: [{ header: "L_EQV_AMT", binding: "bllItmAmt", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllMgmtId", mask: colUtils.getMaskPattern("bllNo") }] },
        { cells: [{ binding: "gfsCd" }] },
        { cells: [{ binding: "itmNm", align: "left" }] },
        { cells: [{ binding: "bllItmAmt", type: "number", thousandSeparator: true, align: "right" }] },
        {
            cells: [{ binding: "bllItmTzsAmt", type: "number", thousandSeparator: true, align: "right" }],
        },
    ],
};

/*
 * @ Form 스키마 정의
 * @ 화면에서 사용하는 Form 구조 정의
 * @ SF_ 로 시작 (Schema form)
 * @ SF_[Form 이름 메타 대문자, "_" 로 구분 ]
 * @ 검색폼은 _SRCH 로 구분
 */

//============================================================================================================================
// 목록
//============================================================================================================================
// 고지 취소 목록 검색
export const SF_BLL_CNCL_LIST_SRCH: TFormSchema = {
    id: "form_bllCnclSrch",
    schema: {
        cstmOfceCd: {
            type: "code",
            label: "L_CSTM_OFCE",
            area: "cstmCd",
            popupSize: "md",
        },
        tansadNo: {
            type: "text",
            label: "L_TANSAD_NO",
            letterCase: "upper",
            mask: colUtils.getMaskPattern("tansadNo"),
        },
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
            letterCase: "upper",
        },
        bllCstmOfceCd: {
            type: "code",
            area: "cstmCd",
            label: "L_CSTM_OFCE",
            edit: false,
        },
        txprTin: {
            type: "text",
            label: "L_TXPR_TIN",
            mask: colUtils.getMaskPattern("tin"),
        },
        dspoDt: {
            type: "daterange",
            label: "L_CNCL_DT",
            start: { name: "dspoStrtDt" },
            end: { name: "dspoEndDt" },
            rangeButton: 0,
            controlSize: 10,
        },
        aprvPrcssStatCd: {
            type: "select",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0096",
        },
        dspoCd: {
            type: "checkbox",
            label: "L_CNCL_CD",
            area: "comnCd",
            comnCd: "OSC0057",
            all: true,
            required: true,
            controlSize: 10,
            excludes: ["G"],
            includes: [{ value: "G", label: "Etc" }],
        },
        cnclStatCd: {
            type: "select",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0252",
            all: true,
        },
    },
};

//============================================================================================================================
// 고지 취소 상세 검색
export const SF_BLL_CNCL_DTL_SRCH: TFormSchema = {
    id: "form_bllCnclDtlSrch",
    schema: {
        srchTp: {
            type: "radio",
            options: [
                { label: "Bill No", value: "B" },
                { label: "TANSAD No", value: "T" },
            ],
            defaultValue: "B",
        },
        bllMgmtId: {
            label: "L_BLL_MGMT_ID",
        },
        bllNo: {
            label: "L_BLL_NO",
        },
        bllSysmCd: {
            type: "text",
            size: 2,
            maxLength: 4,
        },
        bllYy: {
            type: "text",
            size: 1,
            maxLength: 2,
        },
        bllTpCd: {
            type: "text",
            size: 1,
            maxLength: 1,
            letterCase: "upper",
        },
        bllSrno: {
            type: "text",
            size: 3,
            maxLength: 7,
        },
        bllDgr: {
            type: "text",
            size: 1,
            maxLength: 1,
        },
        reffKeyNo: {
            type: "text",
        },
        reffKeyNo1: {
            type: "text",
            size: 2,
            maxLength: 4,
            letterCase: "upper",
            required: true,
        },
        reffKeyNo2: {
            type: "text",
            size: 1,
            maxLength: 2,
            required: true,
        },
        reffKeyNo3: {
            type: "text",
            size: 3,
            maxLength: 7,
            required: true,
        },
    },
};

// 고지 취소 상세
export const SF_BLL_CNCL_DTL: TFormSchema = {
    id: "form_bllCnclDtl",
    schema: {
        bllNo: {
            type: "text",
            label: "L_BLL_NO",
        },
        bllSysmCd: {
            type: "text",
            maxLength: 4,
        },
        bllYy: {
            type: "text",
            maxLength: 2,
        },
        bllTpCd: {
            type: "text",
            maxLength: 1,
        },
        bllSrno: {
            type: "text",
            maxLength: 7,
        },
        bllDgr: {
            type: "text",
            maxLength: 1,
        },
        bllDt: {
            type: "date",
            label: "L_BLL_DT",
        },
        ctrlNo: {
            type: "text",
            label: "L_CTRL_NO",
        },
        zmpDt: {
            type: "text",
            label: "L_ZMP_DT",
        },
        bllTpCdSec: {
            type: "code",
            label: "L_BLL_TP",
            area: "comnCd",
            comnCd: "OSC0058",
        },
        bllStatCd: {
            type: "code",
            label: "L_BLL_STAT",
            area: "comnCd",
            comnCd: "OSC0056",
        },
        pyerMgmtNo: {
            type: "text",
            label: "L_TXPR_TIN",
            mask: colUtils.getMaskPattern("tin"),
        },
        reffKey: {
            type: "text",
            label: "L_REFF_KEY",
        },
        reffKeyTpCd: {
            type: "code",
            label: "L_REFF_TP",
            area: "comnCd",
            comnCd: "OSC0067",
        },
        bllRgsrId: {
            type: "text",
            label: "L_RGSR",
        },
        currCd: {
            type: "select",
            label: "L_CURR_CD",
            area: "comnCd",
            comnCd: "OSC0060",
            edit: false,
        } /* Currency Code	!== 통화 코드 ==! */,
        feeAmt: {
            type: "number",
            label: "L_FEE_AMT",
            thousandSeparator: true,
            decimalScale: 2,
            align: "right",
        },
        feeTzsAmt: {
            type: "number",
            label: "L_FEE_AMT_TZS",
            thousandSeparator: true,
            decimalScale: 2,
            align: "right",
        },
        payDdlnDt: {
            type: "text",
            label: "L_DDLN_DT",
        },
        lastDdlnXtnsDt: {
            type: "text",
            label: "L_DDLN_XTNS_DT",
        },
        xtnsDd: {
            type: "text",
            label: "L_XTNS_DD",
        },
        lastDdlnXtnsRsn: {
            type: "text",
            label: "L_DDLN_XTNS_RSN",
        },
        dspoDt: {
            type: "text",
            label: "L_CNCL_DT",
        },
        dspoCd: {
            type: "code",
            label: "L_CNCL_CD",
            area: "comnCd",
            comnCd: "OSC0057",
        },
        aprvPrcssStatCd: {
            type: "code",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0096",
        },
        cnclStatCd: {
            type: "code",
            label: "L_APRV_STAT_CD",
            area: "comnCd",
            comnCd: "OSC0252",
        },
        dspoRmrk: {
            type: "text",
            label: "L_CNCL_RSN",
        },
    },
};

//============================================================================================================================
// 고지 취소 저장/수정
//============================================================================================================================
// 고지 취소 상세
export const SF_CNCL: TFormSchema = {
    id: "form_cncl",
    schema: {
        dspoCdSave: {
            type: "select",
            label: "L_CNCL_CD",
            area: "comnCd",
            comnCd: "OSC0057",
            select: true,
            required: true,
            excludes: ["H", "M"],
        },
        dspoRmrkSave: {
            type: "textarea",
            label: "L_RMRK",
            required: true,
            controlSize: 10,
        },
    },
};

// Schema of Bill List Grid !== 고지 목록 그리드 스키마 ==!
export const SG_BLL_CNCL_DTL_LIST: TGridSchema = {
    id: "grid_blldDtlList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: 180 }] },
        { cells: [{ header: "L_CTRL_NO", binding: "ctrlNo", width: "*" }] },
        { cells: [{ header: "L_BLL_AMT", binding: "feeAmt", width: "*" }] },
        { cells: [{ header: "L_RCPT_AMT", binding: "rcptAmt", width: "*" }] },
        { cells: [{ header: "L_DUE_DT", binding: "lastPayDdlnDt", width: 100 }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllMgmtId", mask: colUtils.getMaskPattern("bllNo") }] },
        { cells: [{ binding: "ctrlNo" }] },
        { cells: [{ binding: "feeAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "rcptAmt", type: "number", thousandSeparator: true, align: "right" }] },
        { cells: [{ binding: "lastPayDdlnDt", type: "date" }] },
    ],
};

export const SG_BLL_ITM_LIST: TGridSchema = {
    id: "grid_blldDtlList",
    options: {
        index: true, // 그리드 번호  index : true ( asc )  , index : "DESC" ( desc 역순 )
        edit: false, // 그리드 편집 가능 여부 , default : false
        height: "auto",
    },
    head: [
        { cells: [{ header: "L_BLL_NO", binding: "bllNo", width: 180 }] },
        { cells: [{ header: "L_BLL_MGMT_ID", binding: "bllMgmtId", width: 180 }] },
        { cells: [{ header: "L_GFS_CD", binding: "gfsCd", width: "*" }] },
        { cells: [{ header: "L_ITM_NM", binding: "itmNm", width: "2*" }] },
        { cells: [{ header: "L_AMT", binding: "bllItmAmt", width: "*" }] },
        { cells: [{ header: "L_EQV_AMT", binding: "bllItmAmt", width: "*" }] },
    ],
    body: [
        { cells: [{ binding: "bllNo" }] },
        { cells: [{ binding: "bllMgmtId", mask: colUtils.getMaskPattern("bllNo") }] },
        { cells: [{ binding: "gfsCd" }] },
        { cells: [{ binding: "itmNm", align: "left" }] },
        { cells: [{ binding: "bllItmAmt", type: "number", thousandSeparator: true, align: "right" }] },
        {
            cells: [{ binding: "bllItmTzsAmt", type: "number", thousandSeparator: true, align: "right" }],
        },
    ],
};
